<template>
  <div class="space-y-4">
    <!-- Imagen: zona para soltar o elegir, progreso de subida y vista previa. -->
    <div>
      <!-- Se abre desde los botones para tener foco visible y un nombre accesible claro. -->
      <input ref="fileInput" type="file" :accept="accept" class="hidden" tabindex="-1" aria-hidden="true"
             :disabled="uploading" @change="onFileSelected">

      <div v-if="uploading" class="rounded-md border border-gray-200 bg-gray-50 p-4" role="status">
        <p class="flex items-center gap-2 text-sm text-gray-700">
          <font-awesome-icon icon="spinner" class="h-4 w-4 animate-spin motion-reduce:animate-none text-gray-500" aria-hidden="true"/>
          {{ $t('projects.image_uploading', {progress}) }}
        </p>
        <div class="mt-2 h-1.5 w-full overflow-hidden rounded-md bg-gray-200" aria-hidden="true">
          <div class="h-full bg-bpblue motion-safe:transition-all" :style="{width: progress + '%'}"></div>
        </div>
      </div>

      <div v-else-if="previewUrl" class="space-y-2">
        <img :src="previewUrl" :alt="modelValue.alt ?? ''"
             class="block max-h-80 w-auto max-w-full rounded-md border border-gray-200 bg-gray-50 object-contain">
        <button type="button" v-on:click="chooseFile" :aria-describedby="blockId + '-file-help'"
                class="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-bpblue">
          {{ $t('projects.image_replace') }}
        </button>
      </div>

      <p v-else-if="modelValue.file_uuid" class="text-sm text-gray-600">
        {{ $t('projects.image_preview_unavailable') }}
        <button type="button" v-on:click="chooseFile" :aria-describedby="blockId + '-file-help'"
                class="font-medium text-bpblue underline rounded-md hover:text-blue-500 focus:outline-none focus:ring-2 focus:ring-bpblue">
          {{ $t('projects.image_replace') }}
        </button>
      </p>

      <div v-else class="flex flex-col items-center rounded-md border-2 border-dashed px-4 py-6 text-center"
           :class="dragOver ? 'border-bpblue bg-blue-50' : 'border-gray-300'"
           @dragover.prevent="dragOver = true" @dragleave="dragOver = false" @drop.prevent="onDrop">
        <font-awesome-icon icon="image" class="h-6 w-6 text-gray-500" aria-hidden="true"/>
        <button type="button" v-on:click="chooseFile" :aria-describedby="blockId + '-file-help'"
                class="mt-2 inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-bpblue">
          {{ $t('projects.image_choose') }}
        </button>
        <p class="mt-2 text-sm text-gray-600">{{ $t('projects.image_drop_hint') }}</p>
      </div>

      <p :id="blockId + '-file-help'" class="mt-2 text-sm text-gray-600">
        {{ $t('projects.image_file_help', {max: maxMb}) }}
      </p>
      <p v-if="uploadError" class="mt-2 text-sm text-red-600" role="alert">{{ uploadError }}</p>
    </div>

    <div>
      <label :for="blockId + '-alt'" class="block text-sm font-medium text-gray-700">
        {{ $t('projects.image_alt_label') }}
      </label>
      <input :id="blockId + '-alt'" v-model="alt" type="text" maxlength="255" required aria-required="true"
             :aria-describedby="blockId + '-alt-help'"
             class="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-bpblue focus:border-bpblue sm:text-sm">
      <p :id="blockId + '-alt-help'" class="mt-2 text-sm text-gray-600">{{ $t('projects.image_alt_help') }}</p>
    </div>

    <div>
      <label :for="blockId + '-caption'" class="block text-sm font-medium text-gray-700">
        {{ $t('projects.image_caption_label') }}
      </label>
      <input :id="blockId + '-caption'" v-model="caption" type="text" maxlength="500"
             class="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-bpblue focus:border-bpblue sm:text-sm">
    </div>
  </div>
</template>

<script>
import LessonBlockService from "@/services/project/LessonBlockService";
import {IMAGE_ACCEPT, IMAGE_MAX_MB, imageFileError} from "@/views/projects/ProjectEdit/Blocks/imageRules";

/*
 * Bloque de imagen (key `image`, LMS-071). `data` es { file_uuid, alt, caption? }.
 * La imagen se sube al elegirla (POST …/lessons/{lesson}/images) y el bloque
 * guarda el uuid; `preview_url` solo sirve para verla en el editor y no se envía.
 */
export default {
  name: "ImageBlock",
  props: {
    modelValue: {
      type: Object,
      required: true
    },
    blockId: {
      type: String,
      required: true
    },
    lessonUuid: {
      type: String,
      required: true
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      accept: IMAGE_ACCEPT,
      maxMb: IMAGE_MAX_MB,
      uploading: false,
      progress: 0,
      uploadError: null,
      dragOver: false,
    }
  },
  computed: {
    alt: {
      get() {
        return this.modelValue.alt ?? ''
      },
      set(alt) {
        this.$emit('update:modelValue', {...this.modelValue, alt})
      }
    },
    caption: {
      get() {
        return this.modelValue.caption ?? ''
      },
      set(caption) {
        this.$emit('update:modelValue', {...this.modelValue, caption})
      }
    },
    previewUrl() {
      return this.modelValue.preview_url ?? null
    }
  },
  methods: {
    chooseFile() {
      this.$refs.fileInput.click()
    },
    onFileSelected(event) {
      const [file] = event.target.files ?? []
      // Se limpia para que elegir otra vez el mismo fichero vuelva a disparar `change`.
      event.target.value = ''
      if (file) this.upload(file)
    },
    onDrop(event) {
      this.dragOver = false
      const [file] = event.dataTransfer?.files ?? []
      if (file) this.upload(file)
    },
    async upload(file) {
      const errorKey = imageFileError(file)
      if (errorKey) {
        this.uploadError = this.$t(errorKey, {max: this.maxMb})
        return
      }

      this.uploadError = null
      this.uploading = true
      this.progress = 0
      try {
        const response = await LessonBlockService.uploadImage(this.lessonUuid, file, (progress) => {
          this.progress = progress
        })
        const image = response.data?.data ?? {}
        this.$emit('update:modelValue', {...this.modelValue, file_uuid: image.uuid, preview_url: image.url ?? null})
      } catch (error) {
        this.uploadError = this.uploadErrorMessage(error)
      } finally {
        this.uploading = false
      }
    },
    uploadErrorMessage(error) {
      const status = error?.response?.status
      if (status === 429) return this.$t('projects.image_upload_throttled')
      if (status === 422) return error.response.data?.errors?.image?.[0] ?? this.$t('projects.image_upload_error')
      return this.$t('projects.image_upload_error')
    }
  }
}
</script>
